import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { DATESTR_FMT } from 'tdr-common';
import { Info, Trash2, X } from 'lucide-react';
import { buildUnsetParametersMessage } from '../../../helpers/open-parameters/buildUnsetParametersMessage';
import { motion } from 'framer-motion';
import { AvailabilityGuardContext } from '../../../routes/AvailabilityGuard';
import { usePendingParameters } from '../../../context/open-parameters/PendingParametersContext';
import {
	type PendingParameters,
	ParameterField,
	validatePendingParameters
} from '../../../helpers/open-parameters/validatePendingParameters';
import { getLeadGenPartySize } from '../../../helpers/open-parameters/getLeadGenPartySize';
import { getNextUnsetParameterField } from '../../../helpers/open-parameters/getNextUnsetParameterField';
import {
	analytics,
	FILTER_VIEWED,
	OPEN_PARAMETERS_CLEARED,
	OPEN_PARAMETERS_SEARCH_BLOCKED,
	SHOW_RESULTS
} from '../../../helpers/analytics';
import { useFilterAnalytics } from '../../../helpers/analytics/filterContext';
import { useInViewOnce } from '../../../hooks/useInViewOnce';
import { IconButton } from '../../buttons/IconButton';
import { CTAButton } from '../../buttons';
import { MobilePartySizeStepper } from './MobilePartySizeStepper';
import { MobileOpenCalendar } from './MobileOpenCalendar';
import { MobileOpenTimeSelector } from './MobileOpenTimeSelector';
import { MobileParameterSection } from './MobileParameterSection';

type Field = ParameterField;
const AUTO_ADVANCE_DELAY_MS = 350;

const MobileOpenParametersScreen = () => {
	const { t, i18n } = useTranslation();
	const location = useLocation();
	const { restaurant } = useOutletContext<AvailabilityGuardContext>();
	const { pending, setPending, commit, triggerRefresh, reset } = usePendingParameters();
	const { displayMax, leadGenValue } = getLeadGenPartySize(restaurant);

	const [openSection, setOpenSection] = useState<Field | null>('groupSize');
	const [showErrors, setShowErrors] = useState(false);
	const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const rootRef = useRef<HTMLDivElement>(null);
	const filterCtx = useFilterAnalytics('global explore');
	useInViewOnce(rootRef, () => analytics.track(FILTER_VIEWED, { ...filterCtx }));

	useEffect(() => () => {
 if (advanceTimer.current) {
clearTimeout(advanceTimer.current);
}
}, []);

	const scheduleAdvance = (next: Field | null) => {
		if (advanceTimer.current) {
clearTimeout(advanceTimer.current);
}
		advanceTimer.current = setTimeout(() => setOpenSection(next), AUTO_ADVANCE_DELAY_MS);
	};

	const advanceAfter = (field: Field, nextPending: PendingParameters) => {
		scheduleAdvance(getNextUnsetParameterField(nextPending, field));
	};

	const validation = useMemo(() => validatePendingParameters(pending), [pending]);
	const unsetMessage = showErrors ? buildUnsetParametersMessage(validation.errors, t) : null;

	const handleSearch = () => {
		if (validation.status === 'partial') {
			setShowErrors(true);
			analytics.track(OPEN_PARAMETERS_SEARCH_BLOCKED, {
				source: 'open_parameters_mobile',
				missingFields: Object.keys(validation.errors)
			});
			return;
		}
		// "Viewed results": the user filled all params and clicked Search.
		if (validation.status === 'complete') {
			analytics.track(SHOW_RESULTS, {
				source: 'open_parameters_mobile',
				groupSize: pending.groupSize ?? null,
				date: pending.date ?? null,
				time: pending.time ?? null,
				leadGenSelected: leadGenValue !== null && pending.groupSize === leadGenValue
			});
		}
		commit();
		triggerRefresh();
	};

	const handleClearAll = () => {
		analytics.track(OPEN_PARAMETERS_CLEARED, {
			source: 'open_parameters_mobile',
			clearedCount: [pending.groupSize, pending.date, pending.time].filter(Boolean).length
		});
		reset();
		setShowErrors(false);
		setOpenSection('groupSize');
	};

	const guestSummary = pending.groupSize && pending.groupSize > 0
		? (leadGenValue !== null && pending.groupSize === leadGenValue
			? `${pending.groupSize}+`
			: `${pending.groupSize} ${pending.groupSize === 1 ? t('labels.guest', { defaultValue: 'Guest' }) : t('labels.guests', { defaultValue: 'Guests' })}`)
		: t('placeholders.select_party_size', { defaultValue: 'Select a party size' });
	const dateSummary = pending.date
		? DateTime.fromFormat(pending.date, DATESTR_FMT).setLocale(i18n.language === 'fr' ? 'fr-CA' : 'en').toLocaleString({ month: 'short', day: 'numeric' })
		: t('placeholders.select_date', { defaultValue: 'Select a date' });
	const timeSummary = pending.time
		? DateTime.fromFormat(pending.time, 'HH:mm').toFormat('h:mm a').toUpperCase()
		: t('placeholders.select_time', { defaultValue: 'Select a time' });

	const showError = (field: Field) => showErrors && !!validation.errors[field];

	return (
		<motion.div
			ref={rootRef}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.4 }}
			className="bg-tablz-dark-gray-1 w-full sm:w-[400px] h-full flex flex-col"
		>
			<header className="border-b border-white/10 bg-tablz-dark-gray-1 px-4 py-3">
				<div className="grid grid-cols-[40px_1fr_40px] items-center">
					<div aria-hidden className="size-10" />
					<h1 className="text-center font-lora text-[20px] font-medium leading-none text-tablz-text-white-1">
						{t('titles.find_availability', { defaultValue: 'Find Availability' })}
					</h1>
					<IconButton
						icon={X}
						to={{ pathname: '..', search: location.search }}
						variant="secondary"
						className="!size-10 !rounded-lg bg-[#2f3238] text-[#D4D4D8] hover:bg-[#383c44]"
					/>
				</div>
			</header>

			<section className="px-4 py-5 flex flex-col gap-4 grow overflow-y-auto">
				<MobileParameterSection
					title={t('titles.party_size')}
					summary={guestSummary}
					isOpen={openSection === 'groupSize'}
					hasError={showError('groupSize')}
					onToggle={() => setOpenSection(openSection === 'groupSize' ? null : 'groupSize')}
				>
					<MobilePartySizeStepper
						value={pending.groupSize ?? 0}
						max={displayMax}
						leadGenValue={leadGenValue}
						onChange={(n) => {
 setPending({ groupSize: n });
 if (advanceTimer.current) {
clearTimeout(advanceTimer.current);
advanceTimer.current = null;
}
}}
					/>
				</MobileParameterSection>

				<MobileParameterSection
					title={t('labels.date', { defaultValue: 'Date' })}
					summary={dateSummary}
					isOpen={openSection === 'date'}
					hasError={showError('date')}
					onToggle={() => setOpenSection(openSection === 'date' ? null : 'date')}
				>
					<MobileOpenCalendar restaurant={restaurant} value={pending.date} onChange={(d) => {
 const next: PendingParameters = { ...pending, date: d };
 setPending({ date: d });
 advanceAfter('date', next);
}} />
				</MobileParameterSection>

				<MobileParameterSection
					title={t('labels.time', { defaultValue: 'Time' })}
					summary={timeSummary}
					isOpen={openSection === 'time'}
					hasError={showError('time')}
					onToggle={() => setOpenSection(openSection === 'time' ? null : 'time')}
				>
					<MobileOpenTimeSelector restaurant={restaurant} value={pending.time} onChange={(time) => {
 const next: PendingParameters = { ...pending, time };
 setPending({ time });
 advanceAfter('time', next);
}} />
				</MobileParameterSection>

				{unsetMessage ? (
					<div className="flex items-center gap-1.5" data-testid="unset-params-caption">
						<Info size={18} className="shrink-0 text-[#d92d20]" />
						<p className="font-poppins text-xs text-[#d92d20]">{unsetMessage}</p>
					</div>
				) : null}

				<button
					type="button"
					onClick={handleClearAll}
					data-testid="clear-all-button"
					className="mx-auto mt-1 flex items-center gap-2 px-4 py-3 font-poppins text-base font-semibold text-tablz-text-gray-1 hover:text-tablz-text-white-1"
				>
					<Trash2 size={20} />
					{t('actions.clear_all', { defaultValue: 'Clear All' })}
				</button>
			</section>

			<footer className="bg-tablz-dark-gray-2 sticky w-full bottom-0 px-4 py-2 border-t border-tablz-med-gray-4 sm:rounded-b-2xl">
				<CTAButton buttonText={t('actions.search', { defaultValue: 'Search' })} onClick={handleSearch} />
			</footer>
		</motion.div>
	);
};

export default MobileOpenParametersScreen;
